import { Disclosure } from '@headlessui/react'
import { HelixIcon } from '@helix/helix-icon'
import {
  chevron_down as ChevronDown,
  chevron_up as ChevronUp
} from '@helix/helix-icon/outlined'
import { createContext, PropsWithChildren, ReactNode, useContext } from 'react'
import { classNames } from './utils'

interface AccordionProps {
  variant?: 'default' | 'flush'
}

const AccordionContext = createContext<AccordionProps>({
  variant: 'default'
})

export function Accordion({
  children,
  variant = 'default'
}: PropsWithChildren<AccordionProps>) {
  return (
    <AccordionContext.Provider value={{ variant }}>
      <div
        className={classNames('helix-overflow--hidden', {
          'helix-border helix-border--gray-300 helix-border-radius--md':
            variant === 'default'
        })}>
        {children}
      </div>
    </AccordionContext.Provider>
  )
}

interface AccordionButtonProps {
  action?: ReactNode
  disabled?: boolean
  icon?: (props: any) => React.ReactNode
}

function AccordionButton({
  disabled = false,
  children,
  action,
  icon
}: PropsWithChildren<AccordionButtonProps>) {
  const { variant } = useContext(AccordionContext)

  return (
    <div
      className={classNames(
        'helix-w-100-percent helix-d-flex helix-align--start helix-justify--between helix-border-none helix-text--left helix-bg-white helix-body--strong',
        {
          'helix-px-5 helix-py-4 helix-border-bottom helix-border--gray-300':
            variant === 'default'
        }
      )}>
      <span
        className={classNames('relative', {
          'helix-pr-3': variant === 'default',
          'w-full': variant === 'flush'
        })}>
        {children}
        {variant === 'flush' && !disabled && (
          <Disclosure.Button className="absolute right-4 top-1/2 -translate-y-1/2 transform">
            {({ open }) => (
              <HelixIcon
                icon={icon ? icon : open ? ChevronUp : ChevronDown}
                className="helix-shrink-0"
              />
            )}
          </Disclosure.Button>
        )}
      </span>
      {variant === 'default' && (
        <div className="flex items-center gap-4">
          {action}
          <Disclosure.Button>
            {({ open }) => (
              <HelixIcon
                icon={open ? ChevronUp : ChevronDown}
                className="helix-shrink-0"
              />
            )}
          </Disclosure.Button>
        </div>
      )}
    </div>
  )
}

interface AccordionPanelProps {
  noPadding?: boolean
}

function AccordionPanel({
  children,
  noPadding
}: PropsWithChildren<AccordionPanelProps>) {
  const { variant } = useContext(AccordionContext)

  return (
    <Disclosure.Panel
      className={classNames('helix-body', {
        'helix-px-5 helix-py-4': !noPadding,
        'helix-border-bottom helix-border--gray-300': variant === 'default'
      })}>
      {children}
    </Disclosure.Panel>
  )
}

Accordion.Disclosure = Disclosure
Accordion.Button = AccordionButton
Accordion.Panel = AccordionPanel
